// src/components/InterventionsPage.tsx
import React, { useState } from 'react';
import Header from './shared/Header';
import './styles/InterventionsPage.css';

const InterventionsPage: React.FC = () => {
  const [interventions, setInterventions] = useState([
    {
      id: 1,
      title: "Engineering Retention Program",
      department: "Engineering",
      riskLevel: "high",
      employees: 12,
      budget: "$45,000",
      status: "active",
      progress: 65,
      description: "Targeted retention packages for high-risk engineers"
    },
    {
      id: 2,
      title: "Sales Compensation Review",
      department: "Sales",
      riskLevel: "medium",
      employees: 8,
      budget: "$28,000",
      status: "planned",
      progress: 20,
      description: "Competitive analysis and salary adjustments"
    },
    {
      id: 3,
      title: "Leadership Development",
      department: "All",
      riskLevel: "medium",
      employees: 24,
      budget: "$75,000",
      status: "active",
      progress: 40,
      description: "Manager training for employee engagement"
    },
    {
      id: 4,
      title: "Workplace Flexibility Initiative",
      department: "HR",
      riskLevel: "low",
      employees: 45,
      budget: "$15,000",
      status: "completed",
      progress: 100,
      description: "Remote work policy implementation"
    }
  ]);

  const [activeFilter, setActiveFilter] = useState("all");

  const filteredInterventions = interventions.filter(int => {
    if (activeFilter === "all") return true;
    if (activeFilter === "active") return int.status === "active";
    if (activeFilter === "planned") return int.status === "planned";
    if (activeFilter === "completed") return int.status === "completed";
    return true;
  });

  return (
    <div className="interventions-page">
      {/* Prometheus Header with Logo */}
      <Header 
        showNav={true}
        title="PROMETHEUS"
        subtitle="Intervention Management"
      />
      
      <main className="interventions-content">
        {/* Page Header */}
        <div className="page-header">
          <div className="header-left">
            <h1>Risk Intervention Management</h1>
            <p className="page-subtitle">
              Proactive strategies to reduce employee turnover risk
            </p>
          </div>
          <div className="header-right">
            <button className="primary-button">
              + New Intervention Plan
            </button>
          </div>
        </div>
        
        {/* Stats Overview */}
        <div className="intervention-stats">
          <div className="stat-card">
            <div className="stat-icon">���</div>
            <div className="stat-content">
              <div className="stat-value">{interventions.length}</div>
              <div className="stat-label">Active Interventions</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">���</div>
            <div className="stat-content">
              <div className="stat-value">89</div>
              <div className="stat-label">Employees Covered</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">���</div>
            <div className="stat-content">
              <div className="stat-value">$163K</div>
              <div className="stat-label">Total Budget</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">���</div>
            <div className="stat-content">
              <div className="stat-value">42%</div>
              <div className="stat-label">Avg. Risk Reduction</div>
            </div>
          </div>
        </div>
        
        {/* Filter Tabs */}
        <div className="filter-tabs">
          <button 
            className={`tab ${activeFilter === 'all' ? 'active' : ''}`}
            onClick={() => setActiveFilter('all')}
          >
            All Interventions
          </button>
          <button 
            className={`tab ${activeFilter === 'active' ? 'active' : ''}`}
            onClick={() => setActiveFilter('active')}
          >
            Active
          </button>
          <button 
            className={`tab ${activeFilter === 'planned' ? 'active' : ''}`}
            onClick={() => setActiveFilter('planned')}
          >
            Planned
          </button>
          <button 
            className={`tab ${activeFilter === 'completed' ? 'active' : ''}`}
            onClick={() => setActiveFilter('completed')}
          >
            Completed
          </button>
        </div>
        
        {/* Interventions List */}
        <div className="interventions-list">
          {filteredInterventions.map(intervention => (
            <div key={intervention.id} className="intervention-card">
              <div className="card-header">
                <div className="intervention-title">
                  <h3>{intervention.title}</h3>
                  <span className={`risk-badge ${intervention.riskLevel}`}>
                    {intervention.riskLevel.toUpperCase()} RISK
                  </span>
                </div>
                <div className="department-tag">
                  <span className="dept-icon">���</span>
                  {intervention.department}
                </div>
              </div>
              
              <div className="card-content">
                <p className="intervention-description">{intervention.description}</p>
                
                <div className="intervention-metrics">
                  <div className="metric">
                    <div className="metric-label">Employees</div>
                    <div className="metric-value">{intervention.employees}</div>
                  </div>
                  <div className="metric">
                    <div className="metric-label">Budget</div>
                    <div className="metric-value">{intervention.budget}</div>
                  </div>
                  <div className="metric">
                    <div className="metric-label">Status</div>
                    <div className={`status-badge ${intervention.status}`}>
                      {intervention.status}
                    </div>
                  </div>
                </div>
                
                {/* Progress Bar */}
                <div className="progress-section">
                  <div className="progress-header">
                    <div className="progress-label">Implementation Progress</div>
                    <div className="progress-value">{intervention.progress}%</div>
                  </div>
                  <div className="progress-bar">
                    <div 
                      className="progress-fill"
                      style={{ width: `${intervention.progress}%` }}
                    ></div>
                  </div>
                </div>
              </div>
              
              <div className="card-actions">
                <button className="action-button view">View Details</button>
                <button className="action-button edit">Edit Plan</button>
                <button className="action-button report">Generate Report</button>
              </div>
            </div>
          ))}
        </div>
        
        {/* Success Metrics */}
        <div className="success-metrics">
          <h2>Intervention Success Metrics</h2>
          <div className="metrics-grid">
            <div className="success-card">
              <div className="success-icon">���</div>
              <div className="success-content">
                <div className="success-value">-32%</div>
                <div className="success-label">Avg. Risk Score Reduction</div>
              </div>
            </div>
            <div className="success-card">
              <div className="success-icon">���</div>
              <div className="success-content">
                <div className="success-value">$2.1M</div>
                <div className="success-label">Estimated Savings</div>
              </div>
            </div>
            <div className="success-card">
              <div className="success-icon">���</div>
              <div className="success-content">
                <div className="success-value">89%</div>
                <div className="success-label">Employee Satisfaction</div>
              </div>
            </div>
            <div className="success-card">
              <div className="success-icon">���</div>
              <div className="success-content">
                <div className="success-value">24</div>
                <div className="success-label">High-Risk Retained</div>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      {/* Footer with Logo */}
      <footer className="interventions-footer">
        <div className="footer-content">
          <div className="logo-section">
            <div className="footer-logo">
              <img 
                src="/resources/prometheus_logo.jpg" 
                alt="Prometheus"
                style={{ width: '40px', height: '40px', borderRadius: '10px', marginRight: '15px' }}
              />
              <div>
                <div className="footer-title">Prometheus Interventions</div>
                <div className="footer-subtitle">Proactive risk mitigation strategies</div>
              </div>
            </div>
          </div>
          <div className="footer-stats">
            <div className="footer-stat">
              <div className="stat-value">4</div>
              <div className="stat-label">Active Programs</div>
            </div>
            <div className="footer-stat">
              <div className="stat-value">89</div>
              <div className="stat-label">Employees Supported</div>
            </div>
            <div className="footer-stat">
              <div className="stat-value">$2.1M</div>
              <div className="stat-label">ROI Delivered</div>
            </div>
          </div>
        </div>
        <div className="footer-note">
          ��� <strong>Insight:</strong> Proactive interventions have shown 42% higher retention rates compared to reactive measures.
        </div>
      </footer>
    </div>
  );
};

export default InterventionsPage;
